import React, { useState, useEffect } from 'react';
import { Container, Typography, Paper, FormControl, Button, Box, TextField, Snackbar, Alert, Modal, IconButton, Card, CircularProgress, Autocomplete } from '@mui/material';
import { Close as CloseIcon, Delete, Edit, Star, StarBorder } from '@mui/icons-material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import PDF from '../../assets/Images/PDF.svg';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { AttachFile } from '@mui/icons-material';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import axios from 'axios';
import EditFileUpload from './EditFileUpload';
import { useSnackbar } from '../SnackbarContext';
import GlobalVariable from "../../services/Global";

const baseApiUrl = GlobalVariable.BASE_API_URL;

const CustomToolbar = ({ handleAttachFileClick }) => (
  <div id="toolbar">
    <select className="ql-font">
      <option value="sans-serif" selected>
        Sans Serif
      </option>
      <option value="serif">Serif</option>
      <option value="monospace">Monospace</option>
    </select>
    <select className="ql-header">
      <option value="1">Heading 1</option>
      <option value="2">Heading 2</option>
      <option value="">Normal</option>
    </select>
    <button className="ql-bold"></button>
    <button className="ql-italic"></button>
    <button className="ql-underline"></button>
    <button className="ql-strike"></button>
    <button className="ql-list" value="ordered"></button>
    <button className="ql-list" value="bullet"></button>
    <select className="ql-align"></select>
    <button className="ql-link"></button>
    <button className="ql-image"></button>
    <button onClick={handleAttachFileClick}>
      <AttachFile style={{ fontSize: 18 }} />
    </button>
  </div>
);

const modules = {
  toolbar: {
    container: '#toolbar',
  },
};

const formats = [
  'font',
  'header',
  'bold', 'italic', 'underline', 'strike',
  'list', 'bullet',
  'align',
  'link', 'image',
];
const sections = [
  { value: "Cover Page", label: "Cover Page" },
  { value: "Cover Letter", label: "Cover Letter" },
  { value: "Proposal Contents Page", label: "Proposal Contents Page" },
  { value: "Brief Company Introduction", label: "Brief Company Introduction" },
  { value: "Company History and Mission", label: "Company History and Mission" },
  { value: "Company IT Staffing Experience", label: "Company IT Staffing Experience" },
  { value: "Technical Expertise", label: "Technical Expertise" },
  { value: "Past Performance", label: "Past Performance" },
  { value: "Pricing Details", label: "Pricing Details" },
  { value: "References", label: "References" },
];

const EditSectionDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [fileBase64, setFileBase64] = useState('');
  const [fileName, setFileName] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [sectionName, setSectionName] = useState('');
  const [description, setDescription] = useState('');
  const [comment, setComment] = useState('');
  const [rating, setRating] = useState(0);
  const [documentType, setDocumentType] = useState('');
  const [version, setVersion] = useState(1);
  const [attachment, setAttachments] = useState('');
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const { showSnackbar } = useSnackbar();
  const [openDeleteSnackbar, setOpenDeleteSnackbar] = useState(false);
  const [isPdfOpen, setPdfOpen] = useState(false);
  const [errors, setErrors] = useState({ sectionName: '', description: '', documentType: '' });
  const [loading, setLoading] = useState(false);
  const [responseSource, setResponseSource] = useState(false); 



  useEffect(() => {
    const { sectionName, description, attachment, filename, star_rating, rfp_type, version, toggle, comment } = location.state || {};

    const matchedSection = sections.find(
      (s) => s.value.toLowerCase() === (sectionName || '').toLowerCase()
    );

    // If section name exists but not in dropdown, keep the original value; otherwise use matched value or empty string
    setSectionName(matchedSection ? matchedSection.value : (sectionName || ''));
    setDescription(description || '');
    setComment(comment || '');
    setAttachments(attachment || fileBase64);
    setFileName(filename || fileName);
    setRating(star_rating || 0);
    setDocumentType(rfp_type || '');
    setVersion(version || 1);
    // Set response source: if toggle is 'on' or 'On', set to true (AI-generated), otherwise false (saved response)
    setResponseSource(toggle === 'On' ? false : true);
  }, [location.state]);



  const validate = () => {
    let isValid = true;
    const newErrors = { sectionName: '', description: '', documentType: '' };

    if (!sectionName.trim()) {
      newErrors.sectionName = 'Section Name is required';
      isValid = false;
    } else if (sectionName.length < 3) {
      newErrors.sectionName = 'Section Name must be at least 3 characters long';
      isValid = false;
    } else if (!description.trim() || description === '' || description === '<p><br></p>') {
      newErrors.description = 'Description is required';
      isValid = false;
    } else if (description.length < 10) {
      newErrors.description = 'Description must be at least 10 characters long';
      isValid = false;
    }

    if (!documentType.trim()) {
      newErrors.documentType = 'Document Type is required';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };
  const handleSectionNameChange = (e) => {
    setSectionName(e.target.value);
    if (errors.sectionName) {
      setErrors((prevErrors) => ({ ...prevErrors, sectionName: '' }));
    }
  };

  const handleDescriptionChange = (value) => {
    setDescription(value);
    if (errors.description) {
      setErrors((prevErrors) => ({ ...prevErrors, description: '' }));
    }
  };

  const handleUpdate = async () => {
    if (!validate()) return;
    setLoading(true);

    const storedData = JSON.parse(localStorage.getItem('userDetails'));
    const accessToken = storedData?.access_token;
    const userId = storedData?.userDetails?.id;

    try {
      const response = await axios.put(`${baseApiUrl}/contentlibrary/updateclsection`,
        {
          id: id,
          user_id: userId,
          content_name: sectionName,
          content_description: description,
          comment: comment,
          content_file: attachment,
          content_filename: fileName,
          star_rating: rating,
          rfp_type: documentType,
          version: version,
          toggle: !responseSource ? 'On' : 'Off',
        },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (response.status === 200) {
        showSnackbar('Data has been updated successfully');
        setOpenSnackbar(true);
        navigate(`/content-library`, {
          state: {
            row: {
              content_name: sectionName,
              content_description: description,
              content_file: attachment,
              content_filename: fileName,
              star_rating: rating
            }
          },
        });
      }

    } catch (err) {
      showSnackbar('Failed to update data');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
    setOpenDeleteSnackbar(false);
  };

  const togglePdfView = () => {
    setPdfOpen(!isPdfOpen);
  };

  const handleAttachFileClick = () => {
    setOpenDialog(true);
  };
  const handleDeleteAttachment = () => {
    setAttachments('');
    setFileBase64('');
    setFileName('');
    setOpenDeleteSnackbar(true);
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
  };

  return (
    <Container fluid className='rfpDtailsContainer' maxWidth="100%" sx={{ padding: { xs: 1, sm: 2, md: 3 } }}>
      <Box mt={2}>
        <Paper elevation={3} sx={{ padding: { xs: 2, sm: 3, md: 5 }, borderRadius: '15px' }}>
          <Box display="flex" justifyContent="space-between" flexDirection={{ xs: 'column', sm: 'row' }}>
            <Typography variant="h4" gutterBottom className='fontstyleeb'><strong>Edit Section Details</strong></Typography>
            <Button
              variant="outlined"
              onClick={() => navigate(-1)}
              className='buttonbackss Buttons_ESD1 border-primary text-primary text-capitalize rounded-3'
              sx={{ width: { xs: '100%', sm: 'auto' }, marginBottom: { xs: 2, sm: 0 },}}
            >
              Back
            </Button>
          </Box>

          <Box className='formesd'>
            {loading && (
              <Box
                className="circular-progress"
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  zIndex: 1000,
                }}
              >
                <CircularProgress className=" text-primary" />
              </Box>
            )}
            <Box display="flex" gap={3} flexDirection={{ xs: 'column', md: 'row' }} alignItems="flex-start">
              <Box sx={{ width: { xs: '100%', md: '300px' } }}>
            <FormControl fullWidth margin="normal" className="inputfields">
              <Typography variant="h6">
                <strong>Section Name</strong>
              </Typography>
              <Autocomplete
                freeSolo
                disabled
                options={sections}
                getOptionLabel={(option) =>
                  typeof option === 'string' ? option : option.label || ''
                }
                value={
                  sections.find((s) => s.value === sectionName) || (sectionName ? sectionName : null)
                }
                onChange={(event, newValue) => {
                  const value =
                    typeof newValue === 'string'
                      ? newValue
                      : newValue
                        ? newValue.value
                        : '';
                  handleSectionNameChange({
                    target: {
                      name: 'sectionName',
                      value,
                    },
                  });
                }}
                onInputChange={(event, newInputValue, reason) => {
                  if (reason === 'input') {
                    handleSectionNameChange({
                      target: {
                        name: 'sectionName',
                        value: newInputValue,
                      },
                    });
                  }
                }}
                isOptionEqualToValue={(option, value) =>
                  typeof value === 'string'
                    ? option.value === value
                    : option.value === value.value
                }
                popupIcon={<KeyboardArrowDownIcon />}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select section"
                    name="sectionName"
                    error={!!errors.sectionName}
                    helperText={errors.sectionName}
                    InputLabelProps={{ shrink: false }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "10px",
                        height: "45px",
                      },
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: errors.sectionName ? 'red' : 'rgba(0, 0, 0, 0.23)',
                      },
                      "& .MuiOutlinedInput-input": {
                        paddingTop: "10px",
                        paddingBottom: "5px",
                      },
                    }}
                  />
                )}
                sx={{
                  "& .MuiAutocomplete-popupIndicator": {
                    color: "#0175FF",
                  },
                }}
              />
                </FormControl>
              </Box>
              <Box sx={{ width: { xs: '100%', md: '300px' } }}>
            <FormControl fullWidth margin="normal" className="inputfields">
              <Typography variant="h6">
                <strong>Document Type</strong>
              </Typography>
              <Autocomplete
                freeSolo
                disabled
                options={[
                  { value: "staffing", label: "Staffing" },
                  { value: "solutioning", label: "Solutioning" }
                ]}
                getOptionLabel={(option) =>
                  typeof option === 'string' ? option : option.label || ''
                }
                value={
                  documentType ? { value: documentType, label: documentType } : null
                }
                onChange={(event, newValue) => {
                  const value =
                    typeof newValue === 'string'
                      ? newValue
                      : newValue
                        ? newValue.value
                        : '';
                  setDocumentType(value);
                  if (errors.documentType) {
                    setErrors((prevErrors) => ({ ...prevErrors, documentType: '' }));
                  }
                }}
                onInputChange={(event, newInputValue, reason) => {
                  if (reason === 'input') {
                    setDocumentType(newInputValue);
                    if (errors.documentType) {
                      setErrors((prevErrors) => ({ ...prevErrors, documentType: '' }));
                    }
                  }
                }}
                isOptionEqualToValue={(option, value) =>
                  typeof value === 'string'
                    ? option.value === value
                    : option.value === value.value
                }
                popupIcon={<KeyboardArrowDownIcon />}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select Document Type"
                    name="documentType"
                    error={!!errors.documentType}
                    helperText={errors.documentType}
                    InputLabelProps={{ shrink: false }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "10px",
                        height: "45px",
                      },
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: errors.documentType ? 'red' : 'rgba(0, 0, 0, 0.23)',
                      },
                      "& .MuiOutlinedInput-input": {
                        paddingTop: "10px",
                        paddingBottom: "10px",
                      },
                    }}
                  />
                )}
                sx={{
                  "& .MuiAutocomplete-popupIndicator": {
                    color: "#0175FF",
                  },
                }}
              />
                </FormControl>
              </Box>
              
              <Box sx={{ width: { xs: '100%', md: '300px' } }}>
                <FormControl fullWidth margin="normal" className="inputfields">
                  <Typography variant="h6" sx={{ mb: 1.5 }}>
                    <strong>Response Source</strong>
                  </Typography>
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: { xs: 0.75, sm: 1 },
                      mt: 1
                    }}
                  >
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontSize: { xs: '11px', sm: '12px' },
                        color: !responseSource ? '#0175FF' : '#666',
                        fontWeight: !responseSource ? 600 : 400,
                        transition: 'all 0.3s ease'
                      }}
                    >
                      saved response
                    </Typography>
                    <Box
                      onClick={() => setResponseSource(!responseSource)}
                      sx={{
                        position: 'relative',
                        width: { xs: '36px', sm: '44px' },
                        height: { xs: '18px', sm: '22px' },
                        borderRadius: { xs: '9px', sm: '11px' },
                        backgroundColor: responseSource ? '#0175FF' : '#e0e0e0',
                        cursor: 'pointer',
                        transition: 'background-color 0.3s ease',
                        flexShrink: 0,
                        '&:hover': {
                          backgroundColor: responseSource ? '#0156cc' : '#d0d0d0',
                        }
                      }}
                    >
                      <Box
                        sx={{
                          position: 'absolute',
                          top: { xs: '2px', sm: '2px' },
                          left: responseSource 
                            ? { xs: '20px', sm: '24px' }
                            : { xs: '2px', sm: '2px' },
                          width: { xs: '14px', sm: '18px' },
                          height: { xs: '14px', sm: '18px' },
                          borderRadius: '50%',
                          backgroundColor: 'white',
                          transition: 'left 0.3s ease',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                        }}
                      />
                    </Box>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontSize: { xs: '11px', sm: '12px' },
                        color: responseSource ? '#0175FF' : '#666',
                        fontWeight: responseSource ? 600 : 400,
                        transition: 'all 0.3s ease'
                      }}
                    >
                      AI-generated
                    </Typography>
                  </Box>
                </FormControl>
              </Box>
              
              {/* <Box flex={1} marginRight={60}>
                <FormControl fullWidth margin="normal" className="inputfields">
                  <Typography variant="h6" mt={2} mb={1} color="GrayText" className='fontstylee'>Document Type</Typography>
              <Autocomplete
                freeSolo
                options={[
                  { value: "staffing", label: "Staffing" },
                  { value: "solutioning", label: "Solutioning" }
                ]}
                getOptionLabel={(option) =>
                  typeof option === 'string' ? option : option.label || ''
                }
                value={
                  documentType ? { value: documentType, label: documentType } : null
                }
                onChange={(event, newValue) => {
                  const value =
                    typeof newValue === 'string'
                      ? newValue
                      : newValue
                        ? newValue.value
                        : '';
                  setDocumentType(value);
                  if (errors.documentType) {
                    setErrors((prevErrors) => ({ ...prevErrors, documentType: '' }));
                  }
                }}
                onInputChange={(event, newInputValue, reason) => {
                  if (reason === 'input') {
                    setDocumentType(newInputValue);
                    if (errors.documentType) {
                      setErrors((prevErrors) => ({ ...prevErrors, documentType: '' }));
                    }
                  }
                }}
                isOptionEqualToValue={(option, value) =>
                  typeof value === 'string'
                    ? option.value === value
                    : option.value === value.value
                }
                popupIcon={<KeyboardArrowDownIcon />}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select Document Type"
                    name="documentType"
                    error={!!errors.documentType}
                    helperText={errors.documentType}
                    InputLabelProps={{ shrink: false }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "10px",
                        height: "45px",
                      },
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: errors.documentType ? 'red' : 'rgba(0, 0, 0, 0.23)',
                      },
                      "& .MuiOutlinedInput-input": {
                        paddingTop: "10px",
                        paddingBottom: "10px",
                      },
                    }}
                  />
                )}
                sx={{
                  "& .MuiAutocomplete-popupIndicator": {
                    color: "#0175FF",
                  },
                }}
              />
            </FormControl>
              </Box> */}
            </Box>
          </Box>

          <Box>
            <Typography variant="h6">
              <strong>Rating</strong>
            </Typography>
            <Box display="flex" alignItems="center" mt={1}>
              {[1, 2, 3, 4, 5].map((star) => (
                <IconButton
                  key={star}
                  onClick={() => setRating(star)}
                  sx={{ 
                    padding: '4px',
                    '&:hover': {
                      backgroundColor: 'transparent'
                    }
                  }}
                >
                  {star <= rating ? (
                    <Star sx={{ color: '#FFD700', fontSize: 32 }} />
                  ) : (
                    <StarBorder sx={{ color: '#C0C0C0', fontSize: 32 }} />
                  )}
                </IconButton>
              ))}
            </Box>
          </Box>

          <Box mt={3}>
            <Typography variant="h6">
              <strong>Description</strong>
            </Typography>
            <CustomToolbar handleAttachFileClick={handleAttachFileClick} />
            <ReactQuill
              modules={modules}
              formats={formats}
              value={description}
              onChange={handleDescriptionChange}
              style={{ height: '200px', marginBottom: '20px' }}
            />
            {errors.description && (
              <Typography color="error" variant="body2">
                {errors.description}
              </Typography>
            )}
          </Box>
          
          <Box mt={3}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              <strong>Comment</strong>
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              placeholder="Add a comment..."
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "10px",
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "rgba(0, 0, 0, 0.23)",
                  },
                },
              }}
            />
          </Box>
          
          <Box mt={3}>
            {attachment ? (
              <Typography variant="h6">
                <strong>Attachment</strong>
              </Typography>
            ) : null}
          </Box>

          <Card sx={{ width: '200px', justifyItems: 'center' }} >
            {attachment ? (
              <>
                <Typography
                  variant="body1"
                  style={{ color: '#0175FF', margin: '10px', textOverflow: "ellipsis", whiteSpace: "nowrap", overflow: "hidden", width: '100px' }}
                >
                  {fileName}
                </Typography>
                <img src={PDF} alt="PDF Icon" onClick={togglePdfView} style={{ cursor: 'pointer', color: '#0175FF' }} />
                <Typography
                  variant="body1"
                  onClick={togglePdfView}
                  style={{ cursor: 'pointer', color: '#0175FF', margin: '10px', textOverflow: "ellipsis", whiteSpace: "nowrap", overflow: "hidden", width: '100px' }}
                >
                  View File
                </Typography>
                <Box display={'flex'} mt={3}>
                  <IconButton
                    onClick={handleAttachFileClick}
                  >
                    <Edit
                      className='edit-icon'
                    />
                  </IconButton>
                  <IconButton color="error">
                    <Delete
                      onClick={handleDeleteAttachment}
                      className='delete-icon'
                    />
                  </IconButton>
                </Box>
              </>
            ) : (
              <Typography variant="body1"></Typography>
            )}

          </Card>

          <Box className='buttonsrfp' display="flex" justifyContent="flex-end" flexDirection={{ xs: 'column', sm: 'row' }} mt={8}>
            <Button
              variant="outlined"
              onClick={() => navigate(-1)}
              className='border-primary text-primary text-capitalize rounded-3'
              sx={{ width: { xs: '100%', sm: 'auto' }, marginBottom: { xs: 2, sm: 0 }, mx: { sm: 2 } }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              className='bg-primary text-capitalize rounded-3'
              onClick={handleUpdate}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              Update
            </Button>
          </Box>
        </Paper>
      </Box>

      <Modal open={isPdfOpen} onClose={togglePdfView}>
        <Box
          width="80%"
          height="80%"
          margin="auto"
          bgcolor="white"
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          mt="5%"
          p={2}
        >
          <CloseIcon onClick={togglePdfView} style={{ alignSelf: 'flex-end', cursor: 'pointer' }} />
          {attachment && (
            <iframe
              src={attachment}
              width="100%"
              height="100%"
              title="PDF Viewer"
              style={{ border: 'none' }}
            />
          )}
        </Box>
      </Modal>

      {/* <Snackbar
        open={openSnackbar}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
          Section updated successfully!
        </Alert>
      </Snackbar> */}

      <Snackbar
        open={openDeleteSnackbar}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity="warning" sx={{ width: '100%' }}>
          Attached File Deleted
        </Alert>
      </Snackbar>

      <EditFileUpload
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        setFileBase64={(newBase64) => {
          setFileBase64(newBase64);
          setAttachments(newBase64);
          if (attachment) {
            showSnackbar('Attachment updated successfully');
          } else {
            showSnackbar('New file attached successfully');
          }
          setOpenSnackbar(true);
        }}
        setFileName={setFileName}
      />

    </Container>
  );
};

export default EditSectionDetails;